import { Router, UrlTree } from '@angular/router';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, filter, firstValueFrom, map, Observable, of, take, timeout } from 'rxjs';

import { TenantService } from './tenantService';
import { LoginReturnUrlService } from './login-return-url.service';
import { AmplifyService } from '../../amplify/amplify.service';
import { GridStateService } from '../../shared/grid/grid-state.service';
import { RequestsHomeStateService } from '../../requests/requests-home/requests-home-state.service';

import { login, logout } from '../state/actions/authentication.actions';
import { selectUserRole, selectUserToken } from '../state/selector';
import { resetUsers } from '../../users/state/actions/user.actions';

@Injectable({
  providedIn: 'root',
})
export class AuthenticatorService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly tenantService = inject(TenantService);
  private readonly amplifyService = inject(AmplifyService);
  private readonly gridStateService = inject(GridStateService);
  private readonly requestsHomeStateService = inject(RequestsHomeStateService);
  private readonly loginReturnUrl = inject(LoginReturnUrlService);

  constructor() {}

  public login(username: string, password: string): void {
    this.store.dispatch(login({ username, password }));
  }

  public loginWithSSO(): void {
    this.tenantService
      .isSsoEnabled()
      .pipe(take(1))
      .subscribe((enabled) => {
        if (!enabled) {
          console.warn('SSO not enabled for', this.tenantService.tenant);
          return;
        }
        void this.amplifyService.signInWithSSO();
      });
  }

  public logout(): void {
    this.loginReturnUrl.clear();
    this.store.dispatch(logout());
    this.store.dispatch(resetUsers());
    // Session-only view state must not leak into the next login.
    this.gridStateService.clearAll();
    this.requestsHomeStateService.clear();
  }

  // Delegate to AmplifyService to check actual Cognito session
  public async isAuthenticated(): Promise<boolean> {
    return this.amplifyService.isAuthenticated();
  }

  public async getToken(): Promise<string | null> {
    return await firstValueFrom(this.store.select(selectUserToken));
  }

  // Added timeout(5000) to prevent infinite waiting if role never arrives
  getRedirectUrlBasedOnRole(returnUrl: string): Observable<UrlTree> {
    if (returnUrl && returnUrl !== '/') {
      return of(this.router.parseUrl(returnUrl));
    }

    // Wait for the role so guards see a loaded profile before the first navigation.
    return this.store.select(selectUserRole).pipe(
      filter((role): role is string => !!role),
      take(1),
      timeout(5000),
      map(() => this.router.parseUrl('/requests')),
      catchError((error) => {
        console.warn('getRedirectUrlBasedOnRole failed:', error);
        return of(this.router.parseUrl('/requests'));
      })
    );
  }
}
